import type {
  AccessOverrideView,
  AccessRoleView,
  Effect,
  Job,
  Permission,
  PersonAccessView,
  WhoCanView,
} from "@dentacore/shared-types";

import { api } from "./client";

/** Settings → Access (STORY-164 to 167). Every route is clinic-scoped: a role is the clinic's. */

export function listRoles(signal?: AbortSignal): Promise<AccessRoleView[]> {
  return api<AccessRoleView[]>("/access/roles", { signal });
}

export interface RoleWrite {
  name?: string;
  description?: string | null;
  permissions?: Permission[];
  defaultForJobs?: Job[];
}

export function createRole(
  body: RoleWrite & { name: string; permissions: Permission[] },
): Promise<AccessRoleView> {
  return api<AccessRoleView>("/access/roles", { method: "POST", body });
}

export function updateRole(roleId: string, body: RoleWrite): Promise<AccessRoleView> {
  return api<AccessRoleView>(`/access/roles/${roleId}`, { method: "PATCH", body });
}

export function archiveRole(roleId: string): Promise<void> {
  return api<void>(`/access/roles/${roleId}`, { method: "DELETE" });
}

export function installStarterRoles(): Promise<AccessRoleView[]> {
  return api<AccessRoleView[]>("/access/roles/starter-set", { method: "POST" });
}

export function personAccess(userId: string, signal?: AbortSignal): Promise<PersonAccessView> {
  return api<PersonAccessView>(`/access/people/${userId}`, { signal });
}

/** The roles someone holds at one location (R-D6). An empty list gives them their job's role there. */
export function setRoles(
  userId: string,
  branchId: string,
  roleIds: string[],
): Promise<PersonAccessView> {
  return api<PersonAccessView>(`/access/people/${userId}/roles`, {
    method: "PUT",
    body: { branchId, roleIds },
  });
}

/** Everyone still on "Staff (current access)" onto their job's role, location by location (R-D7). */
export function moveToJobRoles(): Promise<{ people: number; places: number }> {
  return api<{ people: number; places: number }>("/access/roles/move-to-job-roles", {
    method: "POST",
  });
}

/** Who can do one thing, at each location, and why. */
export function whoCan(permission: Permission, signal?: AbortSignal): Promise<WhoCanView> {
  return api<WhoCanView>(`/access/who-can?permission=${encodeURIComponent(permission)}`, {
    signal,
  });
}

export function addOverride(
  userId: string,
  body: { permission: Permission; effect: Effect; reason: string; expiresAt: string | null },
): Promise<PersonAccessView> {
  return api<PersonAccessView>(`/access/people/${userId}/overrides`, { method: "POST", body });
}

export function endOverride(overrideId: string): Promise<void> {
  return api<void>(`/access/overrides/${overrideId}/end`, { method: "POST" });
}

export interface OverridesReport {
  overrides: AccessOverrideView[];
  shared: { permission: Permission; effect: Effect; people: number }[];
}

export function overridesReport(signal?: AbortSignal): Promise<OverridesReport> {
  return api<OverridesReport>("/access/overrides", { signal });
}
